import { z } from "zod";
import { TodayTaskSchema } from "./planning";

const InboxOpaqueIdSchema = z.uuid();
const InboxRequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);
const LocalDateSchema = z.iso.date();

export const InboxItemStatusSchema = z.enum([
  "untriaged",
  "planned",
  "routine_created",
  "cancelled",
]);

export const InboxItemSchema = z
  .object({
    id: InboxOpaqueIdSchema,
    workspace_id: InboxOpaqueIdSchema,
    title: z.string().min(1).max(240),
    notes: z.string().max(8000),
    source_type: z.enum(["manual", "mcp", "api"]),
    source_label: z.string().max(160).nullable(),
    created_by_client: z.enum(["web", "mcp", "api"]),
    status: InboxItemStatusSchema,
    resolved_task_id: InboxOpaqueIdSchema.nullable(),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    resolved_at: z.number().int().nullable(),
  })
  .strict();

export const CreateInboxItemRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(240),
    notes: z.string().trim().max(8000).default(""),
  })
  .strict();

export const CreateInboxItemResponseSchema = z
  .object({
    item: InboxItemSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: InboxRequestIdSchema,
  })
  .strict();

export const InboxItemListResponseSchema = z
  .object({
    items: z.array(InboxItemSchema),
    request_id: InboxRequestIdSchema,
  })
  .strict();

export const PlanInboxItemRequestSchema = z
  .object({
    expected_version: z.number().int().min(1),
    child_id: InboxOpaqueIdSchema,
    project_id: InboxOpaqueIdSchema.nullable().default(null),
    title: z.string().trim().min(1).max(240),
    child_copy: z.string().trim().min(1).max(160),
    planned_local_date: LocalDateSchema,
    due_local_date: LocalDateSchema.nullable().default(null),
    estimated_minutes: z.number().int().min(0).max(1440).nullable().default(null),
    priority: z.number().int().min(0).max(4).default(0),
    verification_policy: z
      .enum(["none", "guardian_optional", "guardian_required", "evidence_required"])
      .default("none"),
  })
  .strict()
  .refine(
    (value) => value.due_local_date === null || value.due_local_date >= value.planned_local_date,
    {
      message: "due_local_date must not be earlier than planned_local_date",
      path: ["due_local_date"],
    },
  );

export const PlanInboxItemResponseSchema = z
  .object({
    inbox_item: InboxItemSchema,
    item: TodayTaskSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: InboxRequestIdSchema,
  })
  .strict();

export const CancelInboxItemRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const CancelInboxItemResponseSchema = z
  .object({
    item: InboxItemSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: InboxRequestIdSchema,
  })
  .strict();

export type InboxItem = z.infer<typeof InboxItemSchema>;
export type CreateInboxItemRequest = z.infer<typeof CreateInboxItemRequestSchema>;
export type CreateInboxItemResponse = z.infer<typeof CreateInboxItemResponseSchema>;
export type InboxItemListResponse = z.infer<typeof InboxItemListResponseSchema>;
export type PlanInboxItemRequest = z.infer<typeof PlanInboxItemRequestSchema>;
export type PlanInboxItemResponse = z.infer<typeof PlanInboxItemResponseSchema>;
export type CancelInboxItemRequest = z.infer<typeof CancelInboxItemRequestSchema>;
export type CancelInboxItemResponse = z.infer<typeof CancelInboxItemResponseSchema>;
